import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { fetchItemSizes } from "../components/ui/cards/longCard/apis/itemSize";
import {
  cartKeyId,
  deleteServerCart,
  fetchServerCart,
  getCartKey,
  pushServerCart,
  readClientData,
} from "../features/cart/api/cartStore";
import { sectionFromSource } from "../features/cart/api/designStats";

// How long to wait after the last change before saving the cart. Dragging a
// size dropdown fires a change per keystroke, and one save per client action is
// enough.
const CART_SYNC_DELAY = 600;

// Create the context
const GlobalContext = createContext();

// The size list depends only on the category and does not change while the app
// is open, so the first lookup per category is remembered. Adding ten earrings
// used to fire ten identical requests. Keyed exactly the way add to cart has
// always requested it, without subCategoryName, so it stays separate from the
// list LongCard asks for.
const itemSizeCache = new Map();

// Returns the remembered list, or undefined if this category has never been
// looked up. Lets a caller decide without starting a request.
export const peekItemSizes = (categoryName) => itemSizeCache.get(categoryName);

export const loadItemSizes = async (categoryName) => {
  if (itemSizeCache.has(categoryName)) return itemSizeCache.get(categoryName);
  try {
    const sizes = await fetchItemSizes({
      queryKey: [`item-sizes-${categoryName}`, { categoryName }],
    });
    itemSizeCache.set(categoryName, sizes || []);
    return sizes || [];
  } catch (error) {
    console.error("Error fetching item sizes:", error);
    return [];
  }
};

// Create the provider component
export const GlobalProvider = ({ children }) => {
  const [rightSideBar, setRightSideBar] = useState(false);
  const [proceedClick, setProceedClick] = useState([]);

  const [search, setSearch] = useState("");
  const [infoFilter, setInfoFilter] = useState("");

  const [filterDate, setFilterDate] = useState({
    startDate: null,
    endDate: null,
  });

  const [filterEvent, setFilterEvent] = useState("");

  const [filterModify, setFilterModify] = useState({
    diamondQuality: "",
    goldValue: "",
    rhodium: "",
    size: "",
    categories: [],
  });

  const [details, setDetails] = useState({
    prevPurchase: 0,
    diamondWeight: 0,
    goldWeight: 0,
    designCount: 0,
    categories: 0,
    cartScore: 0,
  });

  const [scanned, setScanned] = useState(false);
  const scrollRef = useRef(null);

  const [homeScreen, setHomeScreen] = useState(false);
  const [clientForm, setClientForm] = useState(() => {
    if (JSON.parse(localStorage.getItem("ClientData"))) {
      return true;
    }
    return false;
  });

  const [cartScreen, setCartScreen] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);

  const [orderScreen, setOrderScreen] = useState(false);
  const [orderDetails, setOrderDetails] = useState(false);
  const [gatiError, setGatiError] = useState(false);

  const [collapse, setCollapse] = useState(false);

  const [payload, setPayload] = useState({
    avgDiaWeight: "",
    avgNetWeight: "",
  });

  const [videoModal, setVideoModal] = useState({
    isOpen: false,
    data: null,
    isScanned: false,
  });

  const [galleryView, setGalleryView] = useState(false);

  const [selectedCard, setSelectedCard] = useState(() => {
    const stored = JSON.parse(localStorage.getItem("cartItems"));
    return stored ? stored[stored.length - 1]?.styleNumber : null;
  });

  const getSessionCart = () => {
    try {
      const cart = localStorage.getItem("cartItems");
      if (!cart) return [];

      const parsed = JSON.parse(cart);

      // Ensure proper structure with defaults
      return parsed.map((item) => ({
        ...item,
        quantity: item.quantity ?? item.quantityData?.length ?? 1,
        quantityData: item.quantityData?.map((q) => ({
          key:
            q.key ||
            `${item.styleNumber}-${Date.now()}-${Math.random()
              .toString(36)
              .substr(2, 5)}`,
          itemSize: q.itemSize ?? "",
          goldColor: q.goldColor ?? "P",
          goldQty: q.goldQty ?? "18",
          diaQty: q.diaQty ?? "VS-GH",
          message: q.message ?? null,
          rhodium: q.rhodium ?? "W",
        })) || [
          {
            key: `${item.styleNumber}-${Date.now()}-${Math.random()
              .toString(36)
              .substr(2, 5)}`,
            itemSize: item?.itemSize || "",
            goldColor: "P",
            goldQty: "18",
            diaQty: "VS-GH",
            message: null,
            rhodium: "W",
          },
        ],
        sizeAvailable: item.sizeAvailable ?? false,
      }));
    } catch (error) {
      console.error("Error parsing cart:", error);
      return [];
    }
  };

  const setSessionCart = (cart) => {
    try {
      if (!cart) return;

      // Deep clone and maintain all properties
      const updatedCart = JSON.parse(JSON.stringify(cart));

      localStorage.setItem("cartItems", JSON.stringify(updatedCart));
    } catch (error) {
      console.error("Error saving cart:", error);
    }
  };

  const [cartData, setCartData] = useState(getSessionCart());
  const [homeData, setHomeData] = useState([]);
  const [fromHome, setFromHome] = useState(() => {
    return JSON.parse(localStorage.getItem("fromHomeData")) || [];
  });

  // ---------------------------------------------------------------- cart sync
  // The cart is kept on the server, one per salesperson per client per event
  // per purchase order,
  // so a cart built at one counter shows up on the next device the same person
  // logs in on, and switching the event brings up that event's own designs.
  // localStorage is still written on every change - every card reads it
  // directly - but it is a cache now, not the record.
  const [cartId, setCartId] = useState(() =>
    cartKeyId(getCartKey(readClientData()))
  );
  // Client and event the cart belongs to, in full. cartId is the same thing
  // flattened to a string, which is what effects compare on.
  const cartKeyRef = useRef(getCartKey(readClientData()));
  const cartIdRef = useRef(cartId);
  // Cart that has already been read from the server. Until this matches cartId
  // nothing is pushed up, or the empty starting cart would wipe the saved one.
  const hydratedIdRef = useRef(null);
  // Set while writing a cart that came from the server, so loading it does not
  // immediately save it straight back.
  const skipSyncRef = useRef(false);
  // What the pending save is about, for the audit trail.
  const pendingActionRef = useRef({ action: "UPDATE" });
  const syncTimerRef = useRef(null);
  // Read inside the save without making every score recalculation save again.
  const detailsRef = useRef(details);
  const scannedRef = useRef(scanned);

  useEffect(() => {
    detailsRef.current = details;
  }, [details]);

  useEffect(() => {
    scannedRef.current = scanned;
  }, [scanned]);

  const markCartAction = (action, styleNumber = null, itemKey = null) => {
    pendingActionRef.current = { action, styleNumber, itemKey };
  };

  // The client and event are picked in half a dozen places, all of which write
  // them to localStorage and none of which notify anyone. Watching the stored
  // value is what keeps this from having to be threaded through all of them.
  useEffect(() => {
    const readKey = () => {
      const nextKey = getCartKey(readClientData());
      const nextId = cartKeyId(nextKey);
      cartKeyRef.current = nextKey;
      if (nextId === cartIdRef.current) return;
      cartIdRef.current = nextId;
      setCartId(nextId);
    };

    const interval = setInterval(readKey, 1000);
    window.addEventListener("storage", readKey);

    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", readKey);
    };
  }, []);

  // Load the cart saved for this client and event. A cart already in this
  // browser with nothing saved on the server is the first run after the switch
  // to server carts, so it is sent up instead of being thrown away.
  useEffect(() => {
    if (!cartId || hydratedIdRef.current === cartId) return;

    let cancelled = false;
    const keyAtLoad = cartKeyRef.current;

    (async () => {
      try {
        const saved = await fetchServerCart(keyAtLoad);
        if (cancelled) return;

        const savedItems = Array.isArray(saved?.cartItems)
          ? saved.cartItems
          : [];

        if (!savedItems.length) {
          // Nothing saved yet. Keep whatever this device is holding and let
          // the save effect push it up.
          hydratedIdRef.current = cartId;
          if (cartData.length) markCartAction("CREATE");
          setCartData((prev) => [...prev]);
          return;
        }

        skipSyncRef.current = true;
        localStorage.setItem("cartItems", JSON.stringify(savedItems));
        localStorage.setItem(
          "fromHomeData",
          JSON.stringify(saved?.fromHome || [])
        );
        if (saved?.scanned) {
          localStorage.setItem("scanned", "true");
        }

        hydratedIdRef.current = cartId;
        setCartData(getSessionCart());
        setFromHome(saved?.fromHome || []);
        setSelectedCard(
          saved?.selectedCard ??
            savedItems[savedItems.length - 1]?.styleNumber ??
            null
        );
        if (saved?.scanned) setScanned(true);
        if (saved?.details && Object.keys(saved.details).length) {
          setDetails((prev) => ({ ...prev, ...saved.details }));
        }
      } catch (error) {
        // Offline, or the request failed. The cached cart in this browser is
        // still usable, and the next change will try to save again.
        console.error("Error loading saved cart:", error);
        hydratedIdRef.current = cartId;
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartId]);

  useEffect(() => {
    // Only update session storage when cartData actually changes
    if (cartData.length > 0 || localStorage.getItem("cartItems")) {
      setSessionCart(cartData);
    }
    localStorage.setItem("fromHomeData", JSON.stringify(fromHome));

    if (!cartId || hydratedIdRef.current !== cartId) return;

    if (skipSyncRef.current) {
      skipSyncRef.current = false;
      return;
    }

    const key = cartKeyRef.current;
    const { action, styleNumber, itemKey } = pendingActionRef.current;
    pendingActionRef.current = { action: "UPDATE" };

    clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      pushServerCart({
        clientCode: key?.clientCode,
        clientName: key?.clientName || "",
        eventTypeId: key?.eventTypeId || "",
        eventName: key?.eventName || "",
        purchaseNo: key?.purchaseNo || "",
        cartItems: cartData,
        fromHome,
        selectedCard,
        scanned: scannedRef.current,
        details: detailsRef.current,
        action,
        styleNumber,
        itemKey,
      }).catch((error) => {
        // The cart is still in localStorage, so the user keeps working and the
        // next change retries the save.
        console.error("Error saving cart:", error);
      });
    }, CART_SYNC_DELAY);

    return () => clearTimeout(syncTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartData, fromHome, selectedCard, cartId]);

  // Empties the cart in both of the places it lives. Clearing the stored keys
  // on their own is not enough: the sidebar renders from cartData, and neither
  // placing an order nor logging out reloads the page, so the in-memory copy
  // survives and keeps showing designs that are no longer in the cart until a
  // manual refresh rebuilds it from storage.
  // Returns everything it cleared, so a caller that clears ahead of a network
  // call can put the cart back if that call turns out to have failed.
  // mode says what to do with the saved copy on the server:
  //   "CLEAR"   - the user emptied the cart, so drop it there too
  //   "ORDERED" - the cart became an order
  //   "LOCAL"   - only this device is being cleared (logout). The saved cart
  //               stays put, which is the whole point of keeping it server side.
  const resetCart = (mode = "LOCAL") => {
    const keyAtReset = cartKeyRef.current;
    const snapshot = {
      cartKey: keyAtReset,
      cartItems: localStorage.getItem("cartItems"),
      fromHomeData: localStorage.getItem("fromHomeData"),
      scanned: localStorage.getItem("scanned"),
      cartData,
      fromHome,
      selectedCard,
      details,
    };

    if (mode !== "LOCAL" && keyAtReset) {
      // The cart being emptied here is also the thing the save effect would
      // push a moment later, so the delete has to win.
      clearTimeout(syncTimerRef.current);
      skipSyncRef.current = true;
      deleteServerCart(keyAtReset, mode).catch((error) => {
        console.error("Error clearing saved cart:", error);
      });
    }

    if (mode === "LOCAL") {
      // Nothing about the saved cart changed, so the next login for this
      // client has to read it again rather than trust this emptied state.
      clearTimeout(syncTimerRef.current);
      skipSyncRef.current = true;
      hydratedIdRef.current = null;
    }

    localStorage.removeItem("cartItems");
    localStorage.removeItem("fromHomeData");
    localStorage.removeItem("scanned");
    setCartData([]);
    setFromHome([]);
    setSelectedCard(null);
    setScanned(false);
    setDetails({
      prevPurchase: 0,
      diamondWeight: 0,
      goldWeight: 0,
      designCount: 0,
      categories: 0,
      cartScore: 0,
    });

    return snapshot;
  };

  // Puts back a cart cleared by resetCart. Only used when an order that was
  // already sent comes back as a failure.
  const restoreCart = (snapshot) => {
    if (!snapshot) return;

    if (snapshot.cartItems != null) {
      localStorage.setItem("cartItems", snapshot.cartItems);
    }
    if (snapshot.fromHomeData != null) {
      localStorage.setItem("fromHomeData", snapshot.fromHomeData);
    }
    if (snapshot.scanned != null) {
      localStorage.setItem("scanned", snapshot.scanned);
    }

    // The cart is going back to what it was, so the saved copy has to be
    // written again - it was deleted when the order was sent.
    if (snapshot.cartKey) {
      cartKeyRef.current = snapshot.cartKey;
      const restoredId = cartKeyId(snapshot.cartKey);
      hydratedIdRef.current = restoredId;
      cartIdRef.current = restoredId;
      setCartId(restoredId);
    }
    skipSyncRef.current = false;
    markCartAction("RESTORE");

    setCartData(snapshot.cartData || []);
    setFromHome(snapshot.fromHome || []);
    setSelectedCard(snapshot.selectedCard ?? null);
    setScanned(!!snapshot.scanned);
    if (snapshot.details) setDetails(snapshot.details);
  };

  // Remove from cart and restore to original
  const handleRemoveFromCart = (card, keyToRemove = null) => {
    // Check if we're on the cart page
    const isCartPage = window.location.pathname === "/cart";

    markCartAction("REMOVE", card?.styleNumber, keyToRemove);

    setCartData((prev) => {
      if (isCartPage && keyToRemove) {
        // On cart page - remove specific quantityData item by key
        const updated = prev
          .map((item) => {
            if (item?.styleNumber === card?.styleNumber) {
              // Filter out the specific quantityData item
              const filteredQuantityData = item?.quantityData.filter(
                (q) => q?.key !== keyToRemove
              );

              // If no more quantityData items left, remove the entire item
              if (filteredQuantityData?.length === 0) {
                return null;
              }

              return {
                ...item,
                quantity: filteredQuantityData?.length,
                quantityData: filteredQuantityData,
              };
            }
            return item;
          })
          .filter(Boolean);

        localStorage.setItem("cartItems", JSON.stringify(updated));
        if (card.styleNumber === selectedCard) {
          setSelectedCard(updated?.[updated.length - 1]?.styleNumber || null);
        }
        return updated;
      } else {
        // Not on cart page - remove entire item (original behavior)
        const newCart = prev.filter((c) => c?._id !== card?._id);

        // If the removed item was selected, pick the new first item from updated cart
        if (card?.styleNumber === selectedCard) {
          setSelectedCard(newCart[newCart.length - 1]?.styleNumber || null);
        }

        localStorage.setItem("cartItems", JSON.stringify(newCart));
        return newCart;
      }
    });
    setVideoModal((prev) => ({ ...prev, isOpen: false, isScanned: false }));

    if (fromHome.includes(card?._id)) {
      setHomeData((prev) =>
        prev.map((cat) =>
          cat?.type === card?.source
            ? {
                ...cat,
                designs: [card, ...cat?.designs],
              }
            : cat
        )
      );
      setFromHome((prev) => prev.filter((id) => id !== card?._id));
    }
  };

  const handleAddToCart = async (card, source) => {
    const categoryName = card?.categoryName;

    markCartAction("CREATE", card?.styleNumber);

    // The size list is only read for the sizeAvailable flag on a brand new
    // row - a design already in the cart never touches it. So the card goes
    // into the store straight away, and if this category has not been looked
    // up yet the flag is confirmed once the request comes back.
    const alreadyInCart = cartData?.some(
      (item) => item?.styleNumber === card?.styleNumber
    );
    const cachedSizes = itemSizeCache.get(categoryName);
    const needsLookup = !alreadyInCart && cachedSizes === undefined;

    // Assume sizes exist until proven otherwise. hasInvalidSize skips items
    // whose sizeAvailable is false, so guessing true keeps the PROCEED size
    // check strict while the flag is unconfirmed, rather than risking an
    // unsized design slipping through.
    const sizeAvailable = cachedSizes ? cachedSizes.length > 0 : true;

    setCartData((prev) => {
      // Check if item already exists in cart
      const existingItemIndex = prev.findIndex(
        (item) => item?.styleNumber === card?.styleNumber
      );

      if (existingItemIndex >= 0) {
        // Item exists - increase quantity and add new quantityData
        const updated = [...prev];
        updated[existingItemIndex] = {
          ...updated[existingItemIndex],
          quantity: updated[existingItemIndex].quantity + 1,
          quantityData: [
            ...updated[existingItemIndex].quantityData,
            {
              key: `${card?.styleNumber}-${Date.now()}-${Math.random()
                .toString(36)
                .substr(2, 5)}`,
              itemSize: card?.subCategoryName === "Without Chain" ? "N/A" : card?.itemSize || "",
              goldColor: "P",
              goldQty: "18",
              diaQty: "VS-GH",
              message: null,
              rhodium: "W",
            },
          ],
        };

        // Update session storage
        localStorage.setItem("cartItems", JSON.stringify(updated));
        setSelectedCard(updated[updated.length - 1]?.styleNumber);
        return updated;
      } else {
        // New item - add to cart with initial quantityData
        const cardWithSource = {
          ...card,
          source,
          // Which part of the portal this design was picked from, and when.
          // Read back when the order is placed to count designs by section.
          // A card that already carries a section keeps it: the image popup
          // and the gallery re-add a row that came from somewhere else.
          addSection: card?.addSection || sectionFromSource(source),
          addedAt: card?.addedAt || new Date().toISOString(),
          quantity: 1,
          sizeAvailable,
          quantityData: [
            {
              key: `${card?.styleNumber}-${Date.now()}-${Math.random()
                .toString(36)
                .substr(2, 5)}`,
              itemSize: card?.itemSize || "",
              goldColor: "P",
              goldQty: "18",
              diaQty: "VS-GH",
              message: null,
              rhodium: "W"
            },
          ],
        };

        const updated = [...prev, cardWithSource];
        localStorage.setItem("cartItems", JSON.stringify(updated));
        setSelectedCard(updated[updated.length - 1]?.styleNumber);
        if (scrollRef?.current) {
          scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
        }
        return updated;
      }
    });

    // Update home data and fromHome state
    setFromHome((prev) => [...prev, card._id]);
    setHomeData((prev) =>
      prev.map((cat) =>
        cat.type === source
          ? {
              ...cat,
              designs: cat.designs.filter((d) => d._id !== card._id),
            }
          : cat
      )
    );

    if (!needsLookup) return;

    // First design added from this category. Confirm the flag in the
    // background, and only touch the cart again if the guess was wrong.
    const sizes = await loadItemSizes(categoryName);
    const confirmed = sizes?.length > 0;
    if (confirmed === sizeAvailable) return;

    setCartData((prev) => {
      const updated = prev.map((item) =>
        item?.styleNumber === card?.styleNumber
          ? { ...item, sizeAvailable: confirmed }
          : item
      );
      localStorage.setItem("cartItems", JSON.stringify(updated));
      return updated;
    });
  };

  const hasInvalidSize = useMemo(() => {
    return cartData?.filter((item) => {
      if (!item?.sizeAvailable) return false;

      if (item?.subCategoryName === "Without Chain") return false;

      return item?.quantityData?.some((qtyItem) => qtyItem?.itemSize === "");
    });
  }, [cartData]);

  return (
    <GlobalContext.Provider
      value={{
        rightSideBar,
        setRightSideBar,
        homeScreen,
        setHomeScreen,
        clientForm,
        setClientForm,
        cartScreen,
        setCartScreen,
        orderScreen,
        setOrderScreen,
        orderComplete,
        setOrderComplete,
        scanned,
        setScanned,
        search,
        setSearch,
        filterDate,
        setFilterDate,
        filterEvent,
        setFilterEvent,
        filterModify,
        setFilterModify,
        details,
        setDetails,
        cartData,
        setCartData,
        homeData,
        setHomeData,
        fromHome,
        setFromHome,
        handleAddToCart,
        handleRemoveFromCart,
        resetCart,
        restoreCart,
        selectedCard,
        setSelectedCard,
        orderDetails,
        setOrderDetails,
        gatiError,
        setGatiError,
        collapse,
        setCollapse,
        setProceedClick,
        proceedClick,
        hasInvalidSize,
        payload,
        setPayload,
        scrollRef,
        videoModal,
        setVideoModal,
        galleryView,
        setGalleryView,
        infoFilter,
        setInfoFilter,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

// Custom hook for consuming the context
export const useGlobalContext = () => {
  return useContext(GlobalContext);
};
